import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Center, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { ShieldOff } from 'lucide-react';
import { ALL_MENU_LINKS } from '@components/sidebar/sidebar-v2';
import { usePermissions } from '@contexts/PermissionsContext';
import { useTheme } from '@contexts/ThemeContext';

/** Matches /prefix and /prefix/... but not /prefix-something-else. */
function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Route-level counterpart of the sidebar's menu filter.
 *
 * Hiding a sidebar entry is not access control — the page still loaded for
 * anyone who typed the URL. This resolves the pathname to the most specific
 * menu entry that owns it and blocks the render unless the user's role granted
 * that exact entry, so `/doc-pabean/draft/12/edit` needs the Draft menu even
 * when Dok Pabean is granted.
 *
 * Paths owned by no menu entry (profile, one-off tools) stay reachable, and
 * legacy users are unaffected — `isFallback` short-circuits everything.
 */
export default function MenuAccessGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { canAccessMenu } = usePermissions();

  const path = pathname.split('?')[0];

  // Most specific menu entry owning this path — longest matching prefix.
  const owner = ALL_MENU_LINKS.filter((link) => matchesPrefix(path, link)).sort((a, b) => b.length - a.length)[0];

  // Deliberately delegates to canAccessMenu rather than reading menuLinks
  // directly: every way of failing open (legacy users, unrestricted roles,
  // ALL/ALL_MST, the Custom Roles escape hatch) then applies to typed URLs and
  // to the sidebar identically, instead of drifting apart.
  if (!owner || canAccessMenu(owner)) return <>{children}</>;

  return <AccessDeniedPage />;
}

function AccessDeniedPage() {
  const { colors: themeColors } = useTheme();

  return (
    <Center style={{ minHeight: '60vh' }}>
      <Stack align="center" gap="md" maw={420} px="md">
        <ThemeIcon size={72} radius="xl" variant="light" color="gray">
          <ShieldOff size={36} />
        </ThemeIcon>
        <Title order={3} ta="center">
          Akses Ditolak
        </Title>
        <Text size="sm" ta="center" c={themeColors.textSecondary}>
          Role Anda tidak memiliki akses ke halaman ini. Silakan hubungi admin perusahaan Anda jika Anda membutuhkan
          akses.
        </Text>
      </Stack>
    </Center>
  );
}

export { MenuAccessGuard };
